import React, { useMemo } from 'react';
import { Carousel, Card } from '../../ui/apple-cards-carousel';
import { AceternityCarousel, type AceternitySlideData } from '../../ui/aceternity-carousel';
import { PlanYourVisitOrbButton } from '../../ui/PlanYourVisitOrbButton';
import { useLanguage } from '../../../contexts/useLanguage';
import { useWebDesignedCart } from '../../../contexts/WebDesignedCartContext';
import { resolveExperienceMode } from '../core/resolveExperienceMode';
import { WEB_HERO_SLIDE_DEFS } from './webHeroCarouselConfig';

export type HomeAppleHeroPlacement = 'hero' | 'section';

const HomeAppleHero: React.FC<{
  /** `hero` = top of page (desktop/pad). `section` = mid-page block (mobile after stays/activities). */
  placement?: HomeAppleHeroPlacement;
}> = ({ placement = 'hero' }) => {
  const { t, language } = useLanguage();
  const cart = useWebDesignedCart();
  const [viewportWidth, setViewportWidth] = React.useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth
  );
  const experienceMode = resolveExperienceMode(viewportWidth);
  const isPhoneView = experienceMode === 'mobile';
  /** Aceternity spotlight carousel on desktop only; mobile + pad keep Apple Cards. */
  const useDesktopSpotlight = experienceMode === 'desktop' && placement === 'hero';

  React.useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openCalendarAssistant = () => {
    cart?.openBookingAssistant();
  };

  const planVisitLabel =
    t('landing.webHero.planYourVisit') || t('common.planYourVisit') || 'Plan your visit';

  /** Mobile / pad: Apple Cards carousel with Plan your visit orb on each card face. */
  const appleCards = useMemo(
    () =>
      WEB_HERO_SLIDE_DEFS.map((def, index) => {
        const title = t(`landing.webHero.slide.${def.id}.title`);
        return (
          <Card
            key={def.id}
            index={index}
            layout
            card={{
              src: def.image,
              title,
              category: def.photoDate,
              faceAction: (
                <PlanYourVisitOrbButton
                  label={planVisitLabel}
                  onClick={openCalendarAssistant}
                  compact
                />
              ),
              content: (
                <div className="space-y-4">
                  <img
                    src={def.image}
                    alt=""
                    className="h-48 w-full rounded-2xl object-cover md:h-72"
                  />
                  <p className="text-base text-neutral-600 md:text-lg">{title}</p>
                  <div className="flex justify-center pt-2">
                    <PlanYourVisitOrbButton
                      label={planVisitLabel}
                      onClick={openCalendarAssistant}
                    />
                  </div>
                </div>
              ),
            }}
          />
        );
      }),
    [t, language, planVisitLabel, cart]
  );

  /** Desktop: Aceternity spotlight — CTA still opens calendar (orb sits under title via button slot). */
  const desktopSlides: AceternitySlideData[] = useMemo(
    () =>
      WEB_HERO_SLIDE_DEFS.map((def) => ({
        title: t(`landing.webHero.slide.${def.id}.title`),
        button: planVisitLabel,
        src: def.image,
        onButtonClick: openCalendarAssistant,
      })),
    [t, language, planVisitLabel, cart]
  );

  const headerPad =
    placement === 'section'
      ? '1.5rem'
      : isPhoneView
        ? 'var(--wd-header-clearance)'
        : useDesktopSpotlight
          ? 'calc(148px + 0.75rem)'
          : 'calc(132px + 0.75rem)';

  return (
    <section
      id={placement === 'section' ? 'home-gallery-carousel' : 'home-hero'}
      className="relative w-full overflow-x-clip overflow-y-visible"
      style={{
        background: '#f3eee7',
        paddingTop: headerPad,
        paddingBottom:
          placement === 'section'
            ? '2rem'
            : isPhoneView
              ? 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)'
              : '4.5rem',
      }}
    >
      {useDesktopSpotlight ? (
        <div className="relative w-full overflow-visible py-2">
          <AceternityCarousel
            slides={desktopSlides}
            planVisitOrb
            nextHintLabel={t('landing.webHero.nextPhoto') || 'Next'}
            prevHintLabel={t('landing.webHero.prevPhoto') || 'Previous'}
          />
        </div>
      ) : (
        <div className="mt-1 md:mt-2">
          <Carousel
            items={appleCards}
            nextHintLabel={t('landing.webHero.nextPhoto') || 'Next photo'}
          />
        </div>
      )}
    </section>
  );
};

export default HomeAppleHero;
